/* eslint-disable no-nested-ternary */
import Loading from '@gp/components/Loading';
import NoData from '@gp/components/NoData';
import TriggerControlBtn from '@gp/components/TriggerControlBtn';
import FixedBottomScrollbar from '@home/components/FixedBottomScrollbar';
import { useMemoizedFn } from 'ahooks';
import classnames from 'classnames';
import React, { useImperativeHandle, useMemo, useRef } from 'react';
import useCalendarCardLayout from './_hooks/useCalendarCardLayout';
import useCalendarCardScrollable from './_hooks/useCalendarCardScrollable';
import useCurrentBtn from './_hooks/useCurrentBtn';
import useFoldable from './_hooks/useFoldable';
import useInitialData from './_hooks/useInitialData';
import CalendarPanelCardList from './components/CalendarPanelCardList';
import CalendarPanelHeader from './components/CalendarPanelHeader';
import CalendarPanelTopbar from './components/CalendarPanelTopbar';
import { DEFAULT_CALENDAR_CARD_SIZE } from './constants';
import { CalendarCardSize } from './enums';
import { getIsDataListEmpty } from './utils';

import type { IBaseProps } from '@home/types/common';

import './index.scss';

// 日历面板组件实例
export interface ICalendarPanelInstance {
  // 日历卡片尺寸change回调
  onCardSizeChange: (size: CalendarCardSize) => void;
  // 重置日历卡片尺寸为默认尺寸
  resetCardSize: () => void;
}

interface ICalendarPanelProps extends IBaseProps {
  // 是否为Studio页面的日历面板
  isStudioCalendar?: boolean;
  // 是否为Pipeline/Dashboard页面的日历面板
  isDashboardCalendar?: boolean;
  // 日历卡片尺寸
  calendarCardSize: CalendarCardSize;
  // 全屏时，日历卡片Card组件内 Bubble的挂载节点
  fullScreenContent: React.RefObject<HTMLElement | null>;
  fixedBottomScrollbarStyle?: React.CSSProperties;
  // 工具栏左侧的内容
  topBarLeftElement?: React.ReactElement | null;
  // 工具栏右侧的内容
  topBarRightElement?: React.ReactElement | null;
  // 是否正在加载数据中
  isLoading: boolean;
  // 是否无权限
  isNoPermission?: boolean;
  // 时间粒度
  dateType: ScheduledateTypes;
  // 日历数据
  data: CommonCalendarDataTypes;
  // 获取页面容器（支持纵向滚动那个dom）
  getPageContainerEl?: () => HTMLElement | null;
  // 点击日历卡片的回调
  onCardClick?:
    | ((cardList: CommonCalendarCardItemTypes | null, isJump: boolean) => string | undefined)
    | null;
}

const CalendarPanel: React.ForwardRefRenderFunction<ICalendarPanelInstance, ICalendarPanelProps> = (
  props,
  ref,
) => {
  const {
    style,
    className,
    isStudioCalendar = false,
    isDashboardCalendar = false,
    calendarCardSize = DEFAULT_CALENDAR_CARD_SIZE,
    fullScreenContent,
    fixedBottomScrollbarStyle,
    topBarLeftElement,
    topBarRightElement,
    isLoading = true,
    isNoPermission = false,
    dateType = 'quarter',
    data = [],
    getPageContainerEl = () => document.querySelector('.dbg-body .tea-layout__content-body'),
    onCardClick,
    reSetFun,
    isSingLine,
    category,
  } = props;

  // 时间轴容器
  const timeLineRef = useRef<HTMLDivElement | null>(null);
  // 日历卡片容器（支持水平滚动那个dom）
  const containerRef = useRef<HTMLDivElement | null>(null);
  // 横向滚动条（模拟滚动条）
  const scrollbarRef = useRef<HTMLDivElement | null>(null);

  const {
    // 当前时间（日历数据中能匹配上当前时间的 时间，没有匹配上的，当前时间为空字符串）
    currentTime,
    // 日历数据（过滤无效数据后的）
    dataList,
  } = useInitialData({
    // 是否正在加载日历数据
    isLoading,
    // 时间粒度
    dateType,
    // 日历数据
    data,
  });

  // 是否无日历数据
  const isNoData = useMemo<boolean>(() => {
    if (isLoading) return false;
    // if (category?.length) return false;
    return getIsDataListEmpty(dataList);
  }, [isLoading, dataList]);

  // 日历卡片布局重新计算的依赖
  const reCalcDeps = useMemo<React.DependencyList>(
    () => [isLoading, isNoData, dataList, calendarCardSize],
    [isLoading, isNoData, dataList, calendarCardSize],
  );

  const {
    // 列间距
    colSpacing,
    // 列信息映射（键为列对应的时间，值为列的信息）
    colCfgMapping,
    // 获取列的marginRight（最后一列marginRight为0）
    getCalendarColMR,
    // 获取日历卡片的marginRight（每一列每一行的最后一个日历卡片的marginRight为0）
    getCalendarCardMR,
    // 日历卡片尺寸change回调
    onCardSizeChange,
    // 重置日历卡片尺寸为默认尺寸
    resetCardSize,
  } = useCalendarCardLayout({
    // 日历卡片容器（支持水平滚动那个dom）
    cardListEl: containerRef,
    // 是否正在加载日历数据
    isLoading,
    // 是否无日历数据
    isNoData,
    // 日历数据
    calendarDataList: dataList,
    // 重新计算日历卡片布局的依赖
    deps: reCalcDeps,
  });

  const {
    // 是否自动定位滚动结束
    isAutoScrollFinished,
    // 日历卡片容器是否水平方向溢出
    isOverflowX,
    // 日历卡片容器最小scrollLeft（当前时间对应的日历卡片在可视区域内）
    min,
    // 日历卡片容器最大scrollLeft（当前时间对应的日历卡片在可视区域内）
    max,
    // 日历卡片外层容器（支持滚动那个dom）最大滚动距离
    maxScrollLeft,
    // 当前时间对应的scrollLeft（即日历卡片容器的scrollLeft为该值时，当前时间则位于可视区域的最左侧）
    currentTimeScrollLeft,
    // 日历卡片容器的scrollLeft
    scrollLeft,
    // 日历卡片容器滚动的回调
    onScroll: onContainerScroll,
  } = useCalendarCardScrollable({
    // 日历卡片容器（支持水平滚动那个dom）
    cardListEl: containerRef,
    // 是否正在加载日历数据
    isLoading,
    // 是否无日历数据
    isNoData,
    // 日历数据
    calendarDataList: dataList,
    // 当前时间（日历数据中能匹配上当前时间的 时间，没有匹配上的，当前时间为空字符串）
    current: currentTime,
    // 列间距
    colSpacing,
    // 列信息映射（键为列对应的时间，值为列的信息）
    colCfgMapping,
    // 重新计算日历卡片布局的依赖
    deps: reCalcDeps,
  });

  const {
    // 是否展示currentBtn，即快速定位到当前时间对应日历那个按钮
    visible,
    // 是否禁用定位到当前时间节点对应的日历卡片列按钮,
    disabled,
    /**
     * currentBtn类型
     * 0: 不展示箭头
     * -1: 展示左箭头
     * 1: 展示右箭头
     */
    currentBtnType,
    /**
     * 点击currentBtn按钮的回调
     * 滚动当前时间节点对应的列到容器最左侧
     */
    onCurrentBtnClick,
  } = useCurrentBtn({
    // 日历卡片外层容器（支持滚动那个dom）
    cardListEl: containerRef,
    // 是否正在加载数据中
    isLoading,
    // 是否无日历数据
    isNoData,
    // 日历卡片容器是否水平方向溢出
    isOverflowX,
    // 是否自动定位滚动结束
    isAutoScrollFinished,
    // 列间距
    colSpacing,
    // 当前时间
    current: currentTime,
    // 日历卡片外层容器（支持滚动那个dom）最大滚动距离
    maxScrollLeft,
    // 当前时间对应的scrollLeft（即日历卡片容器的scrollLeft为该值时，当前时间则位于可视区域的最左侧）
    currentTimeScrollLeft,
    // 日历卡片面板容器当前的scrollLeft值
    scrollLeft,
    // 日历卡片容器最小scrollLeft（当前时间对应的日历卡片在可视区域内）
    min,
    // 日历卡片容器最大scrollLeft（当前时间对应的日历卡片在可视区域内）
    max,
  });

  // 控制日历卡片面板纵向是否折叠
  const {
    // 是否滚动页面到达底部
    isScroll2PageBottom,
    // 是否存在大于4行的日历卡片
    isOverMaxRows,
    // 是否展示折叠/展开按钮
    isShowAllBtn,
    // 是否折叠
    fold,
    // 切换是否折叠
    toggleFold,
  } = useFoldable(false, {
    // 是否正在加载日历数据
    isLoading,
    // 是否无日历数据
    isNoData,
    // 日历数据
    calendarDataList: dataList,
    // 列信息映射（键为列对应的时间，值为列的信息）
    colCfgMapping,
    // 获取页面容器（支持纵向滚动那个dom）
    getPageContainerEl,
  });

  const scrollbarDeps = useMemo<React.DependencyList>(
    () => [isOverflowX, colCfgMapping, dataList],
    [isOverflowX, colCfgMapping, dataList],
  );

  // 时间轴滚动时
  const handleTimeLineScroll = useMemoizedFn((e?: any) => {
    e?.persist();
    const scrollLeftTmp = e?.target?.scrollLeft || 0;
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeftTmp;
    }
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollLeft = scrollLeftTmp;
    }
    onContainerScroll(e);
  });

  // 卡片区域横向滚动时
  const handleCardBodyScroll = (e?: any) => {
    e?.persist();
    const scrollLeftTmp = e?.target?.scrollLeft || 0;
    if (timeLineRef.current) {
      timeLineRef.current.scrollLeft = scrollLeftTmp;
    }
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollLeft = scrollLeftTmp;
    }
    onContainerScroll(e);
  };

  // 模拟的滚动条（吸附在页面底部）滚动时
  const handleScrollbarScroll = (e?: any) => {
    e?.persist();
    const scrollLeftTmp = e?.target?.scrollLeft || 0;
    if (timeLineRef.current) {
      timeLineRef.current.scrollLeft = scrollLeftTmp;
    }
    if (containerRef.current) {
      containerRef.current.scrollLeft = scrollLeftTmp;
    }
    onContainerScroll(e);
  };

  useImperativeHandle(ref, () => ({
    onCardSizeChange,
    resetCardSize,
  }));

  const noDataTextCur = useMemo(() => {
    return (
      <div className="dbg-nodata-div">
        <span className="dbg-nodata-normal-span">{`No data found for the selected filters. Please adjust your filters or click '`}</span>
        <span className="dbg-nodata-reset-a-span" onClick={reSetFun}>
          {'Reset'}
        </span>
        <span className="dbg-nodata-normal-span">{`' to turn all filters off.`}</span>
      </div>
    );
  }, [reSetFun]);

  return (
    <div
      style={style}
      className={classnames('dbg-calendar-panel', className, {
        'is-dashboard-calendar-panel': isDashboardCalendar,
        'dbg-calendar-panel-nodata': isNoData,
      })}
    >
      {/* 顶部操作栏bar，包含currentBtn、及右侧内容*/}
      <CalendarPanelTopbar
        visible={visible}
        disabled={disabled}
        dateType={dateType}
        currentBtnType={currentBtnType as any}
        onScroll2Current={onCurrentBtnClick}
        left={topBarLeftElement}
        right={topBarRightElement}
        isSingLine={isSingLine}
      />
      <div
        className="dbg-schedule-calendar-body"
        style={{
          marginTop: isLoading ? 0 : 12,
          marginBottom: isLoading || isNoData ? 0 : -16,
        }}
      >
        {isLoading ? (
          <Loading loading style={{ height: 200 }} />
        ) : isNoPermission ? (
          <NoData text="No permissions" height={200} constomStyle={{ fontSize: 14 }} />
        ) : (
          <>
            {isNoData ? (
              <NoData text={noDataTextCur} height={200} constomStyle={{ fontSize: 14 }} />
            ) : (
              <div
                className={classnames('dbg-schedule-calendar-body-main', {
                  // 日历卡片纵向超出规定的最大行数后，并且未滚动到页面最底部时，日历卡片容器底部出现阴影，表示往下滚动还有内容
                  shadow: isOverMaxRows && !isScroll2PageBottom,
                })}
              >
                {/* 日历卡片面板头部 时间线 */}
                <CalendarPanelHeader
                  ref={timeLineRef}
                  colCfgMapping={colCfgMapping}
                  dateType={dateType}
                  currentTime={currentTime}
                  dataList={dataList}
                  getCalendarColMR={getCalendarColMR}
                  onTimeLineScroll={handleTimeLineScroll}
                  isSingLine={isSingLine}
                />
                {/* 日历卡片列表*/}
                <CalendarPanelCardList
                  ref={containerRef}
                  fullScreenContent={fullScreenContent}
                  colCfgMapping={colCfgMapping}
                  fold={fold}
                  isStudioCalendar={isStudioCalendar}
                  isDashboardCalendar={isDashboardCalendar}
                  currentTime={currentTime}
                  dataList={dataList}
                  getCalendarColMR={getCalendarColMR}
                  getCalendarCardMR={getCalendarCardMR}
                  onCardBodyScroll={handleCardBodyScroll}
                  onCardClick={onCardClick}
                />
                {/* 模拟滚动条 */}
                <FixedBottomScrollbar
                  style={{ ...fixedBottomScrollbarStyle, marginTop: -8 }}
                  deps={scrollbarDeps}
                  getScrollableEl={() => containerRef.current || null}
                  onScroll={handleScrollbarScroll}
                />
              </div>
            )}
          </>
        )}
      </div>
      {isShowAllBtn && (
        <TriggerControlBtn
          className="schedule-calendar-flod-btn"
          foldText="Show All"
          unFoldText="Show Less"
          isFold={fold}
          onClick={toggleFold}
        />
      )}
    </div>
  );
};

// @ts-ignore
export default React.memo(React.forwardRef(CalendarPanel));
